import { computed, inject, Injectable, InjectionToken, Provider, signal, Signal } from '@angular/core';
import { Authentication } from '../auth';
import { MemberStore } from '../member/member-store';
import { isNullish, nullish, requiredValue, safeResource, startOfToday } from '@nestech/utils';
import { IntegrationApi } from '../danish-pension';
import { Banner } from '@cassiopeia/integration';
import { DateTime } from 'luxon';
import { Environment } from '../environment';
import { NotificationEntity } from '../notification/model/notification.model';
import { NotificationService } from '../notification/service/notification.service';

const isLessThenAMonthOrNeverHidden = (date: Date | nullish) =>
  !date || startOfToday().diff(DateTime.fromJSDate(date)).as('month') < 1;

const isHidden = (hidden: boolean): boolean => (typeof hidden === 'boolean' ? hidden : true);

export const BannerStore = new InjectionToken<BannerStore>('Banner store');
export interface BannerStore {
  readonly banner: Signal<Banner | nullish>;
  readonly notifications: Signal<NotificationEntity[] | nullish>;
  hide(banner: Banner): Promise<void>;
  show(banner: Banner): Promise<void>;
}

export const provideBannerStore = (environment: Environment): Provider => {
  return {
    provide: BannerStore,
    useClass: environment.apiVersions?.find(v => v.api === 'Banner') ? BannerStoreV1 : DummyStore,
  };
};

class DummyStore implements BannerStore {
  banner = signal(null);
  notifications = signal(null);
  async show() {
    /* empty */
  }
  async hide() {
    /* empty */
  }
}

@Injectable()
class BannerStoreV1 implements BannerStore {
  readonly #memberStore = inject(MemberStore);
  readonly #api = inject(IntegrationApi).api;
  readonly #banners = safeResource({
    params: inject(Authentication).sessionId,
    loader: () => this.#api.member.bannersListV1().then(r => r.data),
  });
  readonly banner: Signal<Banner | nullish>;
  readonly notifications: Signal<NotificationEntity[] | nullish>;

  constructor() {
    const notificationService = inject(NotificationService);
    const preferences = this.#memberStore.preferences;
    // Until the preferences arrive, a banner or notification the member has hidden would flash. A failed load
    // counts as arrived, with nothing hidden.
    const settled = computed(() => preferences.hasValue() || !!preferences.error());
    this.banner = computed(() => {
      if (!settled()) return undefined;
      const hideBanners = preferences.value()?.hideBanners ?? {};
      const banners = this.#banners.value() ?? [];
      return banners.find(
        b =>
          (b.initialPlacement === 'Banner' && isNullish(hideBanners[b.name])) ||
          (b.allowedPlacement?.includes('Banner') &&
            !isHidden(hideBanners[b.name]?.hide) &&
            isLessThenAMonthOrNeverHidden(hideBanners[b.name]?.date)),
      );
    });
    this.notifications = computed(() => {
      if (!settled()) return undefined;
      const banners = this.#banners.value() ?? [];
      const banner = this.banner();
      return banners
        .filter(b => banner?.name !== b.name && b.allowedPlacement?.includes('Notification'))
        .map(
          b =>
            ({
              id: `banner-${b.name}`,
              label: requiredValue(b.header, 'Header'),
              notificationCard: {
                id: `banner-${b.name}`,
                header: requiredValue(b.header, 'Header'),
                description: b.notificationDescription,
                isDismissable: b.dismissable,
                image: b.notificationImage,
                onActivate: () => this.show(b),
              },
            }) as NotificationEntity,
        )
        .filter(({ id }) => !notificationService.isNotificationDismissed(preferences.value(), id));
    });
  }

  async hide(banner: Banner) {
    return this.#memberStore.patchPreferences({ hideBanners: { [banner.name]: { hide: true, date: new Date() } } });
  }

  async show(banner: Banner) {
    const currentName = this.banner()?.name;
    const payload = currentName ? { [currentName]: { hide: true, date: new Date() } } : {};
    return this.#memberStore.patchPreferences({
      hideBanners: {
        ...payload,
        [banner.name]: { hide: false, date: new Date() },
      },
    });
  }
}
